import { cargarDescartes } from "./alertasRecurrenciaDescarte";
import { normalizeMonthLocal } from "./cargarOportunidadesVentas";
import { agruparClientesPorEmpresa } from "./empresaContacto";
import { getDolarValue, loadExchangeRates } from "./exchangeRateService";
import { fetchResumenGrupos } from "./getActividadPrecalculada";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getOportunidades } from "./getOportunidades";

/**
 * Alertas de recurrencia comercial — docs/modulos/alertas-recurrencia-comercial.md.
 *
 * Criterio (rework 2026-09-25, definido por Ignacio, valida el Home en producción):
 *
 * 1. Referencia: una empresa (agrupada por 10.276, nunca por fila de `clientes`) que el AÑO
 *    PASADO, ese mismo mes calendario (`mes_cierre`), o bien CERRÓ (100%, aviso "compró", verde) o
 *    bien tuvo una propuesta que quedó en 0% (aviso "le propusiste y no cerró", gris) — nunca las
 *    dos juntas: si hubo cierre, el cierre gana (mismo principio de precedencia que
 *    `determinarEstadoContacto` en `empresaContacto.js`).
 * 2. Es un ESTADO, no una ventana: se enciende 45 días antes del 1° del mes objetivo y sigue
 *    encendido hasta que ese mes TERMINA (no son 8 días — pueden estar 2 o 3 meses simultáneamente
 *    activos, ver `mesesActivos`).
 * 3. Se apaga por CUALQUIERA de cuatro motivos: (a) propuesta abierta (50/75/90) apuntando a ese
 *    mes de ESTE año, (b) cierre (100%) en ese mes de ESTE año, (c) actividad de CRM con esa
 *    empresa registrada DESPUÉS de que el aviso se encendió, (d) descarte manual — tabla
 *    `alertas_recurrencia_descartadas` (migración ESCRITA SIN APLICAR, 10.137); mientras no exista,
 *    esta 4ª vía simplemente no aporta nada (las otras 3 siguen intactas).
 * 4. Sin filtro por ejecutivo: cada usuario ve el portafolio entero de SU país (10.7 ya no aplica
 *    acá — no hay "de quién es" que filtrar). Sin tope: se listan todas, ordenadas por monto.
 *
 * Alcance: `sales` + `sales_contratos` (10.127); `sales_btl` FUERA (mismo recorte que
 * docs/decisiones/2026-09-22-analisis-alertas-recurrencia-comercial.md). Uruguay fuera
 * (10.181/10.267) — lo impone el propio `pais` con que se llama a `cargarDatosAlertasRecurrencia`.
 * Conversión de moneda (10.185, TC del día 15 del mes de cierre) y agrupación por empresa (10.276)
 * sin cambios respecto de la primera versión.
 */

export const MESES_ES = [
  "",
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

const ESTADOS_ABIERTOS = ["50%", "75%", "90%"];
const DIAS_ANTICIPACION = 45;

/**
 * Carga `sales` + `sales_contratos` + `clientes` de un país (oportunidades SIN filtrar por
 * estatus — el 0% también se necesita ahora, punto 1) y, ya con los `client_id` conocidos, la
 * actividad de CRM precalculada (`fetchResumenGrupos`, mismo camino que ya usan el CRM y Mapa de
 * Calor) y los descartes manuales (tabla nueva, puede no existir todavía). Loader LIVIANO a
 * propósito (no reusa el orquestador pesado `cargarOportunidadesVentas` — trae BTL/categorías/
 * subcategorías/intermediarios que este banner de header no necesita): reusa sus mismas piezas
 * (`getOportunidades`, `getContratos`, `normalizeMonthLocal` importado, no una copia) — 10.1/10.27.
 */
export const cargarDatosAlertasRecurrencia = async ({ pais }) => {
  const [oportunidadesData, contratosData, clientesData] = await Promise.all([
    getOportunidades({ pais }),
    getContratos({ pais }),
    getClientes({ pais }),
    loadExchangeRates(),
  ]);

  const oportunidades = (oportunidadesData || [])
    .filter((o) => !o.eliminada)
    .map((o) => ({
      client_id: o.client_id,
      estatus: o.estatus || "0%",
      importe: o.importe,
      moneda_dolar: o.moneda_dolar,
      mes_cierre: normalizeMonthLocal(o.mes_cierre, o.ano || o.ano_cierre, o.created_at, o.mes_cierre_old || o.mes_old),
    }));

  const contratos = (contratosData || [])
    .filter((c) => !c.eliminada)
    .map((c) => ({
      client_id: c.client_id,
      estatus: c.estatus || "50%",
      importe: c.importe_total,
      moneda_dolar: c.moneda_dolar,
      mes_cierre: normalizeMonthLocal(c.mes_cierre, c.ano, c.created_at, c.mes_inicio),
    }));

  const clientes = clientesData || [];
  const clienteIds = clientes.map((c) => c.id);

  const [resumenActividad, descartes] = await Promise.all([fetchResumenGrupos(clienteIds), cargarDescartes({ pais })]);

  return { oportunidades: [...oportunidades, ...contratos], clientes, resumenActividad, descartes };
};

/**
 * Meses actualmente "encendidos": aquellos cuyo 1° cae dentro de los próximos `DIAS_ANTICIPACION`
 * días desde `hoy`, o cuyo mes todavía no terminó. Puede devolver 0, 1, 2 o 3 meses — el estado no
 * es exclusivo, varios meses conviven encendidos a la vez.
 */
const mesesActivos = (hoy) => {
  const base = new Date(hoy);
  base.setHours(0, 0, 0, 0);
  const targets = [];
  for (let n = -3; n <= 3; n++) {
    const day1 = new Date(base.getFullYear(), base.getMonth() + n, 1);
    const ultimoDia = new Date(base.getFullYear(), base.getMonth() + n + 1, 0);
    const activacion = new Date(day1);
    activacion.setDate(activacion.getDate() - DIAS_ANTICIPACION);
    if (base >= activacion && base <= ultimoDia) {
      targets.push({ mes: day1.getMonth() + 1, ano: day1.getFullYear(), activacion });
    }
  }
  return targets;
};

/**
 * Calcula TODAS las alertas de recurrencia vigentes (sin filtrar por ejecutivo, sin tope — puntos
 * 5/6 del rework). Función pura, testeable.
 *
 * @returns {Array<{tipo: 'compro'|'propuso', empresaNorm, empresaDisplay, mesObjetivo, anoObjetivo,
 *   anoPasado, monto, veces}>} ordenado por `monto` descendente.
 */
export const calcularAlertasRecurrencia = ({
  oportunidades,
  clientes,
  resumenActividad = [],
  descartes = null,
  getDolarValueFn = getDolarValue,
  hoy = new Date(),
  pais,
}) => {
  const gruposEmpresa = agruparClientesPorEmpresa(clientes);
  const empresaPorClienteId = new Map();
  for (const [, grupo] of gruposEmpresa) {
    for (const clienteId of grupo.clienteIds) empresaPorClienteId.set(String(clienteId), grupo.empresaNorm);
  }

  // Última actividad de CRM por empresa: MAX(ultima_fecha_del_grupo) entre todos los client_id del grupo.
  const ultimaActividadPorEmpresa = new Map();
  for (const fila of resumenActividad) {
    const empresaNorm = empresaPorClienteId.get(String(fila.cliente_id));
    if (!empresaNorm || !fila.ultima_fecha_del_grupo) continue;
    const actual = ultimaActividadPorEmpresa.get(empresaNorm);
    if (!actual || fila.ultima_fecha_del_grupo > actual) {
      ultimaActividadPorEmpresa.set(empresaNorm, fila.ultima_fecha_del_grupo);
    }
  }

  const descartadas = new Set((descartes || []).map((d) => `${d.empresa_norm}|${d.mes}|${d.ano}`));

  const sumas = new Map(); // `${empresaNorm}|${mes}|${ano}|${'100'|'0'}` -> { suma, veces }
  const abiertasPorGrupo = new Set(); // `${empresaNorm}|${mes}|${ano}` con 50/75/90 apuntando ahí

  for (const o of oportunidades) {
    const empresaNorm = empresaPorClienteId.get(String(o.client_id));
    if (!empresaNorm || !o.mes_cierre) continue;
    const [mesStr, anoStr] = o.mes_cierre.split("-");
    const mes = parseInt(mesStr, 10);
    const ano = parseInt(anoStr, 10);
    if (!mes || !ano) continue;

    if (o.estatus === "100%" || o.estatus === "0%") {
      const esDolar = o.moneda_dolar === true;
      const importe = Number(o.importe) || 0;
      const rate = esDolar ? getDolarValueFn(`${anoStr}-${mesStr}-15`, pais) || 1 : 1;
      const montoLocal = esDolar ? importe * rate : importe;
      const key = `${empresaNorm}|${mes}|${ano}|${o.estatus === "100%" ? "100" : "0"}`;
      const acc = sumas.get(key) || { suma: 0, veces: 0 };
      acc.suma += montoLocal;
      acc.veces += 1;
      sumas.set(key, acc);
    } else if (ESTADOS_ABIERTOS.includes(o.estatus)) {
      abiertasPorGrupo.add(`${empresaNorm}|${mes}|${ano}`);
    }
  }

  const targets = mesesActivos(hoy);
  const alertas = [];
  for (const t of targets) {
    const anoPasado = t.ano - 1;
    for (const [empresaNorm, grupo] of gruposEmpresa) {
      const sum100 = sumas.get(`${empresaNorm}|${t.mes}|${anoPasado}|100`);
      const sum0 = sumas.get(`${empresaNorm}|${t.mes}|${anoPasado}|0`);
      let tipo;
      let base;
      if (sum100) {
        tipo = "compro";
        base = sum100;
      } else if (sum0) {
        tipo = "propuso";
        base = sum0;
      } else {
        continue;
      }

      const keyEsteAno = `${empresaNorm}|${t.mes}|${t.ano}`;
      if (sumas.has(`${keyEsteAno}|100`)) continue; // (b) ya cerró este año
      if (abiertasPorGrupo.has(keyEsteAno)) continue; // (a) propuesta abierta este año
      // (c) actividad de CRM posterior al encendido
      const ultimaActividad = ultimaActividadPorEmpresa.get(empresaNorm);
      if (ultimaActividad && new Date(ultimaActividad) >= t.activacion) continue;
      if (descartadas.has(`${empresaNorm}|${t.mes}|${t.ano}`)) continue; // (d) descartado a mano

      alertas.push({
        tipo,
        empresaNorm,
        empresaDisplay: grupo.empresaDisplay,
        mesObjetivo: t.mes,
        anoObjetivo: t.ano,
        anoPasado,
        monto: Math.round(base.suma),
        veces: base.veces,
      });
    }
  }

  return alertas.sort((a, b) => b.monto - a.monto);
};

/**
 * Motivo de una alerta — SOLO el texto de la razón, neutro (rediseño Radar 2026-09-25: el panel
 * pasa a columnas, empresa y monto van en su propia columna — ver AlertasRecurrenciaBanner). Texto
 * neutro a propósito ("Se propuso", no "Le propusiste"): el aviso lo ve todo el equipo, no siempre
 * habla a quien hizo la propuesta.
 */
export const motivoAlerta = (alerta) => {
  const mes = MESES_ES[alerta.mesObjetivo];
  const sufijo = alerta.veces > 1 ? ` (${alerta.veces}${alerta.tipo === "compro" ? " ventas" : " propuestas"})` : "";
  return alerta.tipo === "compro"
    ? `Compró en ${mes} del año pasado${sufijo}`
    : `Se propuso en ${mes} y no cerró${sufijo}`;
};

/**
 * Formato compacto ($180M / $45K) para el total del encabezado del panel — la suma de los montos
 * YA convertidos (10.185, cada `alerta.monto` sale convertido de `calcularAlertasRecurrencia`), sin
 * volver a tocar la conversión. No hay un formateador compacto centralizado en el proyecto todavía
 * (10.27, nada en `src/utils/` lo resuelve) — acotado a esta única línea del panel; las líneas
 * individuales siguen con `formatAmount`/`useCurrency` completo (10.1).
 */
export const formatMontoCompacto = (monto) => {
  const abs = Math.abs(monto);
  if (abs >= 1_000_000) return `$ ${Math.round(monto / 1_000_000)}M`;
  if (abs >= 1_000) return `$ ${Math.round(monto / 1_000)}K`;
  return `$ ${Math.round(monto)}`;
};
